/* eslint @typescript-eslint/no-explicit-any: 0 */
import React from 'react';
import {
  Text as JssText,
  withDatasourceCheck,
  Placeholder,
  useSitecoreContext,
} from '@sitecore-jss/sitecore-jss-nextjs';
import {
  Grid,
  HeaderTextBlock,
  LayoutContainer,
  PageSection,
  PageSectionContent,
} from '@greystar/designsystem';
import { TextCardBlockProps } from '@greystar/ui';

const TextCardBlock = (props: TextCardBlockProps): JSX.Element => {
  const { sitecoreContext } = useSitecoreContext();
  const { titleFirstPart, titleSecondPart, heading } = props.fields;
  const isEditing = sitecoreContext.pageEditing;

  const {
    backgroundColour,
    topColour,
    bottomColour,
    blocksPerRow,
    subTitleVariant,
    headerDirection,
  } = props.params;

  const topColourDerived = [topColour, backgroundColour].find((c) => c?.trim()) ?? null;
  const bottomColourDerived = bottomColour ?? null;

  const columnMapper = {
    '1': {
      sm: 4,
      md: 8,
      lg: 12,
    },
    '2': {
      sm: 4,
      md: 4,
      lg: 6,
    },
    '3': {
      sm: 4,
      md: 4,
      lg: 4,
    },
  } as const;

  const columnNumber = String(blocksPerRow ?? '3') as keyof typeof columnMapper;
  return (
    <>
      <PageSection
        as="div"
        data-top-colour={topColourDerived}
        topColour={topColourDerived}
        bottomColour={bottomColourDerived}
      >
        <PageSectionContent>
          <HeaderTextBlock direction={headerDirection ?? 'normal'}>
            <HeaderTextBlock.Subtitle
              variant={
                ['alternative', 'primary', 'secondary'].includes(subTitleVariant ?? '')
                  ? (subTitleVariant as 'alternative' | 'primary' | 'secondary')
                  : 'alternative'
              }
              subtitle={<JssText field={titleFirstPart} editable={isEditing} />}
            />
            <HeaderTextBlock.Subtitle
              subtitle={<JssText field={titleSecondPart} editable={isEditing} />}
            />
            <HeaderTextBlock.Header
              header={<JssText field={heading} editable={isEditing} />}
              as={props.params?.headingStyles ?? undefined}
            />
          </HeaderTextBlock>
        </PageSectionContent>
      </PageSection>
      <PageSection
        as="div"
        data-top-colour={topColourDerived}
        topColour={topColourDerived}
        bottomColour={bottomColourDerived}
      >
        <PageSectionContent>
          <LayoutContainer
            flexDirection="row"
            gap="lg"
            flexWrap="wrap"
            justifyContent="center"
            maxWidth="lg"
            margin="auto"
          >
            <Placeholder
              name={`text-card-block-items-${2}`}
              rendering={props.rendering}
              render={(components) => {
                const validItems = components
                  .map((component, idx) => {
                    if (!React.isValidElement(component) || !component.props) return null;

                    return (
                      <Grid.Item
                        key={component.props.rendering?.uid ?? idx}
                        smColumnSpan={columnMapper[columnNumber].sm}
                        mdColumnSpan={columnMapper[columnNumber].md}
                        lgColumnSpan={columnMapper[columnNumber].lg}
                      >
                        {component}
                      </Grid.Item>
                    );
                  })
                  .filter(Boolean) as React.ReactElement[];

                return <Grid rowGap="lg">{validItems}</Grid>;
              }}
            />
          </LayoutContainer>
        </PageSectionContent>
      </PageSection>
    </>
  );
};

export default withDatasourceCheck()<TextCardBlockProps>(TextCardBlock);
